import {COLORS, applyAlpha} from './color_palettes';

export interface ColorTheme {
  primary: string;
  onPrimary: string;

  primaryContainer: string;
  onPrimaryContainer: string;

  primaryFixed: string;
  primaryFixedDim: string;
  onPrimaryFixed: string;
  onPrimaryFixedVariant: string;

  primaryVar: string;
  onPrimaryVar: string;

  // Secondary
  secondary: string;
  onSecondary: string;

  secondaryContainer: string;
  onSecondaryContainer: string;

  secondaryFixed: string;
  secondaryFixedDim: string;
  onSecondaryFixed: string;
  onSecondaryFixedVariant: string;

  // Tertiary
  tertiary: string;
  onTertiary: string;

  tertiaryContainer: string;
  onTertiaryContainer: string;

  tertiaryFixed: string;
  tertiaryFixedDim: string;
  onTertiaryFixed: string;
  onTertiaryFixedVariant: string;

  // Error
  error: string;
  onError: string;

  errorContainer: string;
  onErrorContainer: string;

  errorFixed: string;
  errorFixedDim: string;
  onErrorFixed: string;
  onErrorFixedVariant: string;

  // Surface
  surface: string;
  surfaceVariant: string;
  surfaceBright: string;
  surfaceDim: string;

  surfaceContainer: string;
  surfaceContainerLow: string;
  surfaceContainerLowest: string;
  surfaceContainerHigh: string;
  surfaceContainerHighest: string;

  onSurface: string;
  onSurfaceVariant: string;
  onSurfaceInverse: string;

  outline: string;
  outlineVariant: string;
}

/**
 * Contrast colors that visually differs from each other and, for example, can
 * be used for drawing many curves on the same plot.
 * TODO: b/415152857 - migrate to new colors.
 */
const COLORS_POOL = [
  COLORS.Blue.get(500),
  COLORS.Magenta.get(500),
  COLORS.Green.get(500),
  COLORS.Yellow.get(500),
  COLORS.Orange.get(500),
  COLORS.Red.get(500),
  COLORS.Purple.get(500),
  COLORS.Blue.get(900),
  COLORS.Magenta.get(900),
  COLORS.Green.get(900),
  COLORS.Yellow.get(900),
  COLORS.Orange.get(900),
  COLORS.Red.get(900),
  COLORS.Purple.get(900),
];

export const RED = COLORS.NewRed.get(50);
export const RED_UNSELECTED = COLORS.NewRed.get(80);
export const RED_SELECTED = COLORS.NewRed.get(30);

export const ORANGE = COLORS.NewOrange.get(50);
export const ORANGE_UNSELECTED = COLORS.NewOrange.get(80);
export const ORANGE_SELECTED = COLORS.NewOrange.get(30);

export const YELLOW = COLORS.NewYellow.get(50);
export const YELLOW_UNSELECTED = COLORS.NewYellow.get(80);
export const YELLOW_SELECTED = COLORS.NewYellow.get(30);

export const OLIVE = COLORS.NewOlive.get(50);
export const OLIVE_UNSELECTED = COLORS.NewOlive.get(80);
export const OLIVE_SELECTED = COLORS.NewOlive.get(30);

export const LIME = COLORS.NewLime.get(50);
export const LIME_UNSELECTED = COLORS.NewLime.get(80);
export const LIME_SELECTED = COLORS.NewLime.get(30);

export const GREEN = COLORS.NewGreen.get(50);
export const GREEN_UNSELECTED = COLORS.NewGreen.get(80);
export const GREEN_SELECTED = COLORS.NewGreen.get(30);

export const CYAN = COLORS.NewCyan.get(50);
export const CYAN_UNSELECTED = COLORS.NewCyan.get(80);
export const CYAN_SELECTED = COLORS.NewCyan.get(30);

export const STEEL_BLUE = COLORS.NewSteelBlue.get(50);
export const STEEL_BLUE_UNSELECTED = COLORS.NewSteelBlue.get(80);
export const STEEL_BLUE_SELECTED = COLORS.NewSteelBlue.get(30);

export const BLUE = COLORS.NewBlue.get(50);
export const DARKER_BLUE = COLORS.NewBlue.get(30);
export const LIGHTER_BLUE = COLORS.NewBlue.get(70);
export const BLUE_UNSELECTED = COLORS.NewBlue.get(80);
export const BLUE_SELECTED = COLORS.NewBlue.get(30);

export const INDIGO = COLORS.NewIndigo.get(50);
export const INDIGO_UNSELECTED = COLORS.NewIndigo.get(80);
export const INDIGO_SELECTED = COLORS.NewIndigo.get(30);

export const PURPLE = COLORS.NewPurple.get(50);
export const PURPLE_UNSELECTED = COLORS.NewPurple.get(80);
export const PURPLE_SELECTED = COLORS.NewPurple.get(30);

export const MAGENTA = COLORS.NewMagenta.get(50);
export const MAGENTA_UNSELECTED = COLORS.NewMagenta.get(80);
export const MAGENTA_SELECTED = COLORS.NewMagenta.get(30);

export const NEUTRAL_30 = COLORS.NewNeutral.get(30);
export const NEUTRAL_10 = COLORS.NewNeutral.get(10);

// Keep aligned with frontend/styles/tokens.scss.
export const COLOR_THEME: Record<string, ColorTheme> = {
  LIGHT: {
    // Primary
    primary: COLORS.NewPrimary.get(40),
    onPrimary: '#fff',

    primaryContainer: COLORS.NewPrimary.get(95),
    onPrimaryContainer: COLORS.NewPrimary.get(30),

    primaryFixed: COLORS.NewPrimary.get(90),
    primaryFixedDim: COLORS.NewPrimary.get(80),
    onPrimaryFixed: COLORS.NewPrimary.get(20),
    onPrimaryFixedVariant: COLORS.NewPrimary.get(30),

    primaryVar: COLORS.NewPrimary.get(99),
    onPrimaryVar: COLORS.NewSecondary.get(10),

    // Secondary
    secondary: COLORS.NewSecondary.get(40),
    onSecondary: '#fff',

    secondaryContainer: COLORS.NewSecondary.get(95),
    onSecondaryContainer: COLORS.NewSecondary.get(30),

    secondaryFixed: COLORS.NewSecondary.get(90),
    secondaryFixedDim: COLORS.NewSecondary.get(80),
    onSecondaryFixed: COLORS.NewSecondary.get(20),
    onSecondaryFixedVariant: COLORS.NewSecondary.get(30),

    // Tertiary
    tertiary: COLORS.NewTertiary.get(40),
    onTertiary: '#fff',

    tertiaryContainer: COLORS.NewTertiary.get(95),
    onTertiaryContainer: COLORS.NewTertiary.get(30),

    tertiaryFixed: COLORS.NewTertiary.get(90),
    tertiaryFixedDim: COLORS.NewTertiary.get(80),
    onTertiaryFixed: COLORS.NewTertiary.get(20),
    onTertiaryFixedVariant: COLORS.NewTertiary.get(30),

    // Error
    error: COLORS.NewError.get(40),
    onError: '#fff',

    errorContainer: COLORS.NewError.get(95),
    onErrorContainer: COLORS.NewError.get(30),

    errorFixed: COLORS.NewError.get(90),
    errorFixedDim: COLORS.NewError.get(80),
    onErrorFixed: COLORS.NewError.get(20),
    onErrorFixedVariant: COLORS.NewError.get(30),

    // Surface
    surface: '#fff',
    surfaceVariant: '#e8ebf2',
    surfaceBright: '#fff',
    surfaceDim: '#d3dbe5',

    surfaceContainer: COLORS.NewNeutralVariant.get(99),
    surfaceContainerLow: COLORS.NewNeutral.get(99),
    surfaceContainerLowest: '#fcfcfc',
    surfaceContainerHigh: COLORS.NewNeutralVariant.get(95),
    surfaceContainerHighest: '#dde3ea',

    onSurface: COLORS.NewNeutral.get(10),
    onSurfaceVariant: COLORS.NewNeutral.get(30),
    onSurfaceInverse: '#f2f2f2',

    outline: COLORS.NewNeutral.get(50),
    outlineVariant: COLORS.NewNeutral.get(80),
  },
  DARK: {
    // Primary
    primary: COLORS.NewPrimary.get(80),
    onPrimary: COLORS.NewPrimary.get(20),

    primaryContainer: COLORS.NewPrimary.get(30),
    onPrimaryContainer: COLORS.NewPrimary.get(90),

    primaryFixed: COLORS.NewPrimary.get(20),
    primaryFixedDim: COLORS.NewPrimary.get(30),
    onPrimaryFixed: COLORS.NewPrimary.get(90),
    onPrimaryFixedVariant: COLORS.NewPrimary.get(80),

    primaryVar: COLORS.NewPrimary.get(10),
    onPrimaryVar: COLORS.NewPrimary.get(99),

    // Secondary
    secondary: COLORS.NewSecondary.get(80),
    onSecondary: COLORS.NewSecondary.get(20),

    secondaryContainer: COLORS.NewSecondary.get(30),
    onSecondaryContainer: COLORS.NewSecondary.get(90),

    secondaryFixed: COLORS.NewSecondary.get(10),
    secondaryFixedDim: COLORS.NewSecondary.get(70),
    onSecondaryFixed: COLORS.NewSecondary.get(90),
    onSecondaryFixedVariant: COLORS.NewSecondary.get(80),

    // Tertiary
    tertiary: COLORS.NewTertiary.get(80),
    onTertiary: COLORS.NewTertiary.get(20),

    tertiaryContainer: COLORS.NewTertiary.get(30),
    onTertiaryContainer: COLORS.NewTertiary.get(90),

    tertiaryFixed: COLORS.NewTertiary.get(10),
    tertiaryFixedDim: COLORS.NewTertiary.get(30),
    onTertiaryFixed: COLORS.NewTertiary.get(90),
    onTertiaryFixedVariant: COLORS.NewTertiary.get(80),

    // Error
    error: COLORS.NewError.get(80),
    onError: COLORS.NewError.get(20),

    errorContainer: COLORS.NewError.get(30),
    onErrorContainer: COLORS.NewError.get(90),

    errorFixed: COLORS.NewError.get(10),
    errorFixedDim: COLORS.NewError.get(30),
    onErrorFixed: COLORS.NewError.get(90),
    onErrorFixedVariant: COLORS.NewError.get(80),

    // Surface
    surface: '#131314',
    surfaceBright: '#37393B',
    surfaceDim: '#131314',
    surfaceVariant: '#131314',

    surfaceContainer: '#1e1f20',
    surfaceContainerLow: '#1b1b1b',
    surfaceContainerLowest: '#0e0e0e',
    surfaceContainerHigh: '#282a2c',
    surfaceContainerHighest: '#333537',

    onSurface: COLORS.NewNeutral.get(95),
    onSurfaceVariant: COLORS.NewNeutralVariant.get(90),
    onSurfaceInverse: '#303030',

    outline: COLORS.NewNeutral.get(60),
    outlineVariant: COLORS.NewNeutral.get(40),
  },
};

/**
 * Creates a generator for a color rotation tool.
 */
export class ColorGenerator {
  index = 0;

  /**
   * Returns the next color out of the predefined list. Updates the current list
   * index, so that next time nextColor() is called, it returns the different
   * color.
   */
  nextColor(alpha = 0): string {
    const color = COLORS_POOL[this.index % COLORS_POOL.length];
    this.index += 1;
    return alpha ? applyAlpha(color, alpha) : color;
  }
}
